import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";
import IsoscelesTrapezoidDiagram
  from "../../../../components/diagrams/IsoscelesTrapezoidDiagram";

const DEC_TOL = 0.01;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; }

// wyciągamy największy kwadrat: n = s^2 * r, r — square-free
function squareFactorOut(n) {
  let r = Math.max(0, Math.round(n));
  let s = 1;
  for (let p = 2; p * p <= r; p++) {
    while (r % (p * p) === 0) {
      r = Math.floor(r / (p * p));
      s *= p;
    }
  }
  return [s, r];
}

/* ===== HELPERY – ułamki dokładne ===== */

// skracanie ułamka z kontrolą znaku
function simplify(n, d) {
  if (d < 0) { n = -n; d = -d; }
  const g = gcd(n, d);
  return [n / g, d / g];
}

// \dfrac{n}{d} albo samo n, gdy d=1
function fracToLatex(n, d) {
  if (d === 1) return `${n}`;
  return `\\dfrac{${n}}{${d}}`;
}

// liczba mieszana (np. 7/2 -> 3 \dfrac{1}{2}); dla poprawnych ułamków zostawia zwykły
function fracToMixedLatex(n, d) {
  const sign = n < 0 ? -1 : 1;
  n = Math.abs(n);
  const k = Math.floor(n / d);
  const r = n % d;
  const head = (sign < 0 ? "-" : "") + (k > 0 ? `${k}` : "");
  if (r === 0) return head || "0";
  const tail = `\\dfrac{${r}}{${d}}`;
  return k > 0 ? `${head}\\;${tail}` : (sign < 0 ? `-\\,${tail}` : tail);
}

/* ========================================= */

export function generateTask() {
  // zakresy
  const A = Array.from({ length: 7 }, (_, i) => i + 6);  // 6..12 (górna)
  const B = Array.from({ length: 9 }, (_, i) => i + 6);  // 6..14 (dolna)
  const C_CAP = 15;
  const R_MAX = 0.55;

  for (let tries = 0; tries < 200; tries++) {
    const a = pick(A);
    // wymaganie: b ≥ a + 3
    const possibleB = B.filter((v) => v >= a + 3);
    if (possibleB.length === 0) continue;
    const b = pick(possibleB);

    const x = (b - a) / 2;

    // bazowe ograniczenia
    const cMinGeom = Math.ceil(x + 1); // h>0
    const cMaxGeom = Math.min(C_CAP, Math.floor(Math.sqrt(x * x + (R_MAX * b) ** 2)));

    // przycięcie do [3,10] (poszerzone)
    const low = Math.max(3, cMinGeom);
    const high = Math.min(10, cMaxGeom);

    if (high < low) continue;

    const C = [];
    for (let v = low; v <= high; v++) C.push(v);
    const c = pick(C);

    return { type: "TrapezoidAreaIsoscelesFromSidesTask", data: { a, b, c } };
  }

  // fallback
  return { type: "TrapezoidAreaIsoscelesFromSidesTask", data: { a: 10, b: 13, c: 5 } };
}

export function renderTask({ data }) {
  const { a, b, c } = data;
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      Dany jest trapez równoramienny o podstawach{" "}
      <InlineMath math={`a=${a}`} /> (górna),{" "}
      <InlineMath math={`b=${b}`} /> (dolna) oraz ramieniu{" "}
      <InlineMath math={`c=${c}`} />. Oblicz jego pole <InlineMath math={'P'} />.
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="P"
      placeholder="np. ((a+b)/4)*√(4c²-(b-a)²)"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const { a, b, c } = data;
  const expected = ((a + b) / 4) *
    Math.sqrt(Math.max(0, 4 * c * c - (b - a) * (b - a)));

  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }
  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - expected) <= DEC_TOL;
}

// — Odpowiedź: wspólna kreska ułamkowa —
export const renderAnswer = ({ data }) => {
  const { a, b, c } = data;

  // R = 4c^2 - (b-a)^2
  const R = 4 * c * c - (b - a) * (b - a);
  const [s, r] = squareFactorOut(R); // R = s^2 * r

  // K = ((a+b)/4) * s  → jako ułamek num/den
  let num = (a + b) * s;
  let den = 4;
  const g = gcd(num, den);
  num /= g; den /= g;

  if (r === 1) {
    // P = num/den
    return den === 1
      ? <InlineMath math={`P=${num}`} />
      : <InlineMath math={`P=\\dfrac{${num}}{${den}}`} />;
  }

  // P = (num * sqrt(r)) / den
  return den === 1
    ? <InlineMath math={`P=${num}\\,\\sqrt{${r}}`} />
    : <InlineMath math={`P=\\dfrac{${num}\\,\\sqrt{${r}}}{${den}}`} />;
};

// — Wyjaśnienie z adaptacyjnym marginesem i wspólną kreską ułamkową —
export function renderExplanation({ data }) {
  const { a, b, c } = data;

  // x = (b-a)/2 jako ułamek (i ewentualnie mieszany do prezentacji)
  const xNumRaw = b - a;
  const xDenRaw = 2;
  const [xNum, xDen] = simplify(xNumRaw, xDenRaw);

  // h^2 = c^2 - x^2 = c^2 - (xNum/xDen)^2 = (c^2*xDen^2 - xNum^2) / xDen^2
  const c2 = c * c;
  const x2Num = xNum * xNum;
  const x2Den = xDen * xDen;
  const h2Num = c2 * x2Den - x2Num; // licznik h^2
  const h2Den = x2Den;               // mianownik h^2

  // h = sqrt(h2Num/h2Den) = sqrt(h2Num) / xDen, uproszczenie sqrt(h2Num)
  const [s, r] = squareFactorOut(h2Num);      // h2Num = s^2 * r
  const [hCoeffNum, hCoeffDen] = simplify(s, xDen);

  // — adaptacyjny margines nad rysunkiem (8..28 px w zależności od h) —
  const xDec = (b - a) / 2;
  const hDec = Math.sqrt(Math.max(0, c * c - xDec * xDec));
  const R_MAX = 0.55;
  const hMax = R_MAX * b;
  const hNorm = Math.max(0, Math.min(1, hDec / hMax));
  const marginTopPx = Math.round(8 + 20 * hNorm);
  const marginBottomPx = -40;

  // latex-y pomocnicze
  const xLatexFrac  = fracToLatex(xNum, xDen);
  const xLatexMixed = (Math.abs(xNum) > xDen) ? fracToMixedLatex(xNum, xDen) : null;

  // Krok 2 – Pitagoras + przekształcenie
  const h2Line0 = `c^2=h^2+x^2`;
  const h2Line1 = `h^2=c^2-x^2`;
  const h2Line2 = `h^2=${c}^2-\\left(${xLatexFrac}\\right)^2`;
  const h2Line3 = `h^2=${c2}-\\dfrac{${x2Num}}{${x2Den}}`;
  const h2Line4 = `h^2=\\dfrac{${c2}\\cdot ${x2Den}-${x2Num}}{${x2Den}}=\\dfrac{${h2Num}}{${h2Den}}`;

  // Wspólna kreska ułamkowa w końcowym zapisie h
  const hLine =
    r === 1
      ? `h=\\sqrt{\\dfrac{${h2Num}}{${h2Den}}}=\\dfrac{\\sqrt{${h2Num}}}{${xDen}}=\\dfrac{${hCoeffNum}}{${hCoeffDen}}`
      : `h=\\sqrt{\\dfrac{${h2Num}}{${h2Den}}}=\\dfrac{\\sqrt{${h2Num}}}{${xDen}}=\\dfrac{${hCoeffNum}\\,\\sqrt{${r}}}{${hCoeffDen}}`;

  // Krok 3: P = ((a+b)/2) * h — z dokładnym rachunkiem
  let Pnum = (a + b) * hCoeffNum;
  let Pden = 2 * hCoeffDen;
  [Pnum, Pden] = simplify(Pnum, Pden);

  const Pline1 = `P=\\dfrac{a+b}{2}\\,h`;
  const Pline2 = r === 1
    ? `P=\\dfrac{${a}+${b}}{2}\\cdot \\dfrac{${hCoeffNum}}{${hCoeffDen}}`
    : `P=\\dfrac{${a}+${b}}{2}\\cdot \\dfrac{${hCoeffNum}\\,\\sqrt{${r}}}{${hCoeffDen}}`;

  // final z jedną kreską ułamkową
  const Pfinal = r === 1
    ? (Pden === 1 ? `P=${Pnum}` : `P=\\dfrac{${Pnum}}{${Pden}}`)
    : (Pden === 1 ? `P=${Pnum}\\,\\sqrt{${r}}` : `P=\\dfrac{${Pnum}\\,\\sqrt{${r}}}{${Pden}}`);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, display: "grid", gap: 10 }}>
      <div><strong>Krok 1.</strong> Odcięta (różnica połówek podstaw) i schemat:</div>

      {/* Adaptowane odstępy nad/pod rysunkiem */}
      <div style={{ marginTop: marginTopPx, marginBottom: marginBottomPx }}>
        <div style={{ display: "grid", placeItems: "center", padding: "14px 0" }}>
          <div style={{ width: "min(100%, 420px)" }}>
            <IsoscelesTrapezoidDiagram
              a={a}
              b={b}
              c={c}
              size={420}
              padTop={68}
              padBottom={28}
              padX={28}
            />
          </div>
        </div>
      </div>

      {/* x jako ułamek (i ewentualnie liczba mieszana) */}
      <BlockMath math={`x=\\dfrac{b-a}{2}=\\dfrac{${b}-${a}}{2}=${xLatexFrac}${xLatexMixed ? `=${xLatexMixed}` : ""}`} />

      <div><strong>Krok 2.</strong> W trójkącie prostokątnym (ramię <InlineMath math={'c'} />, przyprostokątna <InlineMath math={'x'} />, wysokość <InlineMath math={'h'} />):</div>
      <BlockMath math={h2Line0} />
      <BlockMath math={h2Line1} />
      <BlockMath math={h2Line2} />
      <BlockMath math={h2Line3} />
      <BlockMath math={h2Line4} />
      <BlockMath math={hLine} />

      <div><strong>Krok 3.</strong> Pole trapezu:</div>
      <BlockMath math={Pline1} />
      <BlockMath math={Pline2} />
      <BlockMath math={Pfinal} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(raw, "P")} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
